import clsx from 'clsx';
import { ChipLink } from '../../components/ui';

/**
 * One job, four pages — Plan, List, Parts, Scans — and the same row of chips at the top
 * of every one of them, so moving between them is a click in a place that never moves
 * rather than a trip back to the jobs list.
 *
 * The page you are on stays in the row rather than dropping out: a row that changes
 * shape per page is a row you have to re-read every time. The header does not repeat the
 * page's name after the job's, so this chip is the only "you are here" — hence dark, the
 * primary-button ink, rather than a colour that could be mistaken for a job's or a station's.
 */
export function JobNav({
  jobId,
  current,
  partsBatchId,
}: {
  jobId: string;
  current: 'plan' | 'list' | 'parts' | 'scans';
  /**
   * Parts are per batch, so the chip needs one. A job with several batches has no
   * single answer — those jobs keep the Parts button on each batch card instead, where
   * it says which batch it opens.
   */
  partsBatchId?: string;
}) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Chip to={`/jobs/${jobId}`} label="Plan" active={current === 'plan'} />
      <Chip to={`/jobs/${jobId}/list`} label="List" active={current === 'list'} />
      {partsBatchId ? (
        <Chip
          to={`/jobs/${jobId}/batches/${partsBatchId}/parts`}
          label="Parts"
          active={current === 'parts'}
        />
      ) : null}
      <Chip to={`/jobs/${jobId}/scans`} label="Scans" active={current === 'scans'} />
    </span>
  );
}

function Chip({ to, label, active }: { to: string; label: string; active: boolean }) {
  if (active) {
    return (
      <span
        aria-current="page"
        className={clsx(
          'cursor-default rounded bg-ink-900 px-2.5 py-1 text-sm font-semibold text-white shadow-sm',
        )}
      >
        {label}
      </span>
    );
  }
  return (
    <ChipLink to={to} size="md" title={`Open the ${label.toLowerCase()}`}>
      {label}
    </ChipLink>
  );
}
